/*
 * The rep's task when a lead offers their own scheduling page (`docs/2026-10-01-lead-scheduling-link.md`,
 * "The rep's task"; migration 088). The app never books and never fetches the link: the rep opens the
 * lead's page in their own browser, books it for the campaign's call host, and records what happened.
 * Every outcome is the rep's statement (`receipt_basis = 'rep_attested'`), because no proof of a booking
 * on a lead's page ever reaches the app (`docs/2026-10-01-lead-scheduler-booking-spike.md`).
 *
 * Its own tables, so a rollback build, which has never heard of them, reads every plan as before.
 */
import type { Pool, PoolClient } from "pg";
import { z } from "zod";
import {
  LEAD_LINK_ACTION,
  LeadLinkRecordSchema,
} from "../../../skills/leadcue-reply/scripts/lib/lead-scheduling-link.ts";
import { SCHEDULING_STEP_ASKS } from "../../../skills/leadcue-reply/scripts/lib/reply-classification.ts";
import type { Run } from "../../../skills/leadcue-reply/scripts/lib/run-contracts.ts";
import type { WorkspaceScope } from "./workspace-scope.ts";

/**
 * Why a booking on the lead's page did not happen, each a control or form feature the spike documented,
 * and what the rep can do next.
 */
export const COULD_NOT_BOOK_REASONS = [
  "code_to_other_inbox",
  "unanswerable_questions",
  "routed_or_refused",
  "no_suitable_time",
  "not_a_scheduler",
  "payment_required",
  "org_only",
] as const;
export const CouldNotBookReasonSchema = z.enum(COULD_NOT_BOOK_REASONS);
export type CouldNotBookReason = z.infer<typeof CouldNotBookReasonSchema>;

const instant = z.iso.datetime({ offset: true });
export const LeadLinkTaskSchema = z.strictObject({
  planId: z.string().min(1),
  campaignId: z.string().min(1),
  providerReplyId: z.number().int().positive(),
  runId: z.string().min(1),
  url: LeadLinkRecordSchema.shape.url,
  scheduler: LeadLinkRecordSchema.shape.scheduler,
  placement: LeadLinkRecordSchema.shape.placement,
  /** The reply's other ask labels, never its text: what the lead also asked, for the sheet to name. */
  otherAsks: z.array(z.string()),
  createdAt: instant,
  claimedBy: z.string().nullable(),
  claimedAt: instant.nullable(),
  outcome: z.enum(["booked", "could_not_book", "sent_our_link"]).nullable(),
  couldNotBookReason: CouldNotBookReasonSchema.nullable(),
  outcomeBy: z.string().nullable(),
  outcomeAt: instant.nullable(),
  hostName: z.string().nullable(),
  hostEmail: z.string().nullable(),
  bookedStart: instant.nullable(),
  bookedZone: z.string().nullable(),
  bookedMinutes: z.number().int().nullable(),
});
export type LeadLinkTask = z.infer<typeof LeadLinkTaskSchema>;

export const CallHostSchema = z.strictObject({
  name: z.string().trim().min(1).max(200),
  email: z.email().max(320),
});
export type CallHost = z.infer<typeof CallHostSchema>;

const utc = (column: string, alias: string) =>
  `to_char(${column} AT TIME ZONE 'UTC','YYYY-MM-DD"T"HH24:MI:SS.MS"Z"') AS "${alias}"`;
const TASK_COLUMNS = `plan_id AS "planId",campaign_id AS "campaignId",
  provider_reply_id AS "providerReplyId",run_id AS "runId",url,scheduler,placement,
  other_asks AS "otherAsks",${utc("created_at", "createdAt")},claimed_by AS "claimedBy",
  ${utc("claimed_at", "claimedAt")},outcome,could_not_book_reason AS "couldNotBookReason",
  outcome_by AS "outcomeBy",${utc("outcome_at", "outcomeAt")},host_name AS "hostName",
  host_email AS "hostEmail",${utc("booked_start", "bookedStart")},booked_zone AS "bookedZone",
  booked_minutes AS "bookedMinutes"`;

/** A row as read: Postgres returns `bigint` as text. */
function task(row: unknown): LeadLinkTask {
  const value = z.record(z.string(), z.unknown()).parse(row);
  return LeadLinkTaskSchema.parse({ ...value, providerReplyId: Number(value["providerReplyId"]) });
}

/**
 * Writes the task for a run whose lead-link rule applied, in the projection's transaction. A turn has
 * one task: projecting the same run again adds nothing.
 */
export async function recordLeadLinkTask(
  client: PoolClient,
  scope: WorkspaceScope,
  input: { planId: string; campaignId: string; run: Run },
): Promise<void> {
  const record = input.run.lead_link;
  if (record?.state !== "applied") return;
  if (!input.run.actions.some((action) => action.type === LEAD_LINK_ACTION)) return;
  const scheduling = new Set<string>(SCHEDULING_STEP_ASKS);
  const otherAsks = (input.run.analysis?.classification?.asks ?? []).filter(
    (ask) => !scheduling.has(ask),
  );
  await client.query(
    `INSERT INTO lead_link_tasks
       (organization_id,workspace_id,campaign_id,plan_id,provider_reply_id,run_id,url,scheduler,
        placement,other_asks)
     VALUES ($1,$2,$3,$4,$5,$6,$7,$8,$9,$10)
     ON CONFLICT (organization_id,plan_id,provider_reply_id) DO NOTHING`,
    [
      scope.organizationId,
      scope.workspaceId,
      input.campaignId,
      input.planId,
      input.run.input.trigger.id,
      input.run.id,
      record.url,
      record.scheduler,
      record.placement,
      otherAsks,
    ],
  );
}

/**
 * The time the rep booked, entered as the wall time the lead's page showed and the zone it showed it
 * in, as an instant. Null when the zone is not a real IANA zone or the time does not exist there.
 */
export function zonedWallTimeToInstant(local: string, zone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/u.exec(local);
  if (!match) return null;
  let format: Intl.DateTimeFormat;
  try {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return null;
  }
  const [year, month, day, hour, minute] = match.slice(1).map(Number);
  if (
    year === undefined ||
    month === undefined ||
    day === undefined ||
    hour === undefined ||
    minute === undefined
  )
    return null;
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const shown = (at: number): number => {
    const parts = Object.fromEntries(
      format.formatToParts(new Date(at)).map((part) => [part.type, part.value]),
    );
    return Date.UTC(
      Number(parts["year"]),
      Number(parts["month"]) - 1,
      Number(parts["day"]),
      Number(parts["hour"]),
      Number(parts["minute"]),
    );
  };
  // Two passes settle the offset across a change of daylight saving time.
  let guess = wall - (shown(wall) - wall);
  guess = wall - (shown(guess) - guess);
  return shown(guess) === wall ? new Date(guess).toISOString() : null;
}

export class LeadLinkTaskStore {
  constructor(private readonly pool: Pool) {}

  /** The task of the plan's current run, or null when that run left none. */
  async current(
    scope: WorkspaceScope,
    planId: string,
    runId: string,
  ): Promise<LeadLinkTask | null> {
    const result = await this.pool.query(
      `SELECT ${TASK_COLUMNS} FROM lead_link_tasks
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND run_id=$4`,
      [scope.organizationId, scope.workspaceId, planId, runId],
    );
    return result.rows[0] ? task(result.rows[0]) : null;
  }

  async callHost(scope: WorkspaceScope, campaignId: string): Promise<CallHost | null> {
    const result = await this.pool.query(
      `SELECT name,email FROM campaign_call_hosts
       WHERE organization_id=$1 AND workspace_id=$2 AND campaign_id=$3`,
      [scope.organizationId, scope.workspaceId, campaignId],
    );
    return result.rows[0] ? CallHostSchema.parse(result.rows[0]) : null;
  }

  /**
   * Sets who takes the campaign's calls. A sending mailbox is refused: nobody reads it, so an
   * invitation sent there is lost.
   */
  async setCallHost(
    scope: WorkspaceScope,
    campaignId: string,
    host: CallHost,
    actorId: string,
  ): Promise<CallHost | "sending_mailbox"> {
    const value = CallHostSchema.parse(host);
    const email = value.email.trim().toLowerCase();
    const sending = await this.pool.query(
      `SELECT 1 FROM seller_sender_accounts
       WHERE organization_id=$1 AND workspace_id=$2 AND address=$3 LIMIT 1`,
      [scope.organizationId, scope.workspaceId, email],
    );
    if (sending.rows.length) return "sending_mailbox";
    await this.pool.query(
      `INSERT INTO campaign_call_hosts (organization_id,workspace_id,campaign_id,name,email,set_by)
       VALUES ($1,$2,$3,$4,$5,$6)
       ON CONFLICT (organization_id,workspace_id,campaign_id)
       DO UPDATE SET name=EXCLUDED.name,email=EXCLUDED.email,set_by=EXCLUDED.set_by,set_at=now()`,
      [scope.organizationId, scope.workspaceId, campaignId, value.name, email, actorId],
    );
    return { name: value.name, email };
  }

  /** The first rep to open the lead's page owns the task; a second sees who and when. */
  async open(scope: WorkspaceScope, planId: string, runId: string, actorId: string) {
    await this.pool.query(
      `UPDATE lead_link_tasks SET claimed_by=$5,claimed_at=now()
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND run_id=$4
         AND claimed_by IS NULL`,
      [scope.organizationId, scope.workspaceId, planId, runId, actorId],
    );
    return this.current(scope, planId, runId);
  }

  /**
   * The page would not take a booking. It does not close the task: the rep may still book once the
   * call host reads them a code, or send the seller's link instead. A booking already recorded stands.
   */
  async couldNotBook(
    scope: WorkspaceScope,
    planId: string,
    runId: string,
    reason: CouldNotBookReason,
    actorId: string,
  ) {
    await this.pool.query(
      `UPDATE lead_link_tasks SET outcome='could_not_book',could_not_book_reason=$5,
         outcome_by=$6,outcome_at=now()
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND run_id=$4
         AND outcome IS DISTINCT FROM 'booked'`,
      [scope.organizationId, scope.workspaceId, planId, runId, reason, actorId],
    );
    return this.current(scope, planId, runId);
  }

  /** The rep chose the seller's link instead; the redraft that follows writes it. */
  async sentOurLink(scope: WorkspaceScope, planId: string, runId: string, actorId: string) {
    await this.pool.query(
      `UPDATE lead_link_tasks SET outcome='sent_our_link',could_not_book_reason=NULL,
         outcome_by=$5,outcome_at=now()
       WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND run_id=$4
         AND outcome IS DISTINCT FROM 'booked'`,
      [scope.organizationId, scope.workspaceId, planId, runId, actorId],
    );
    return this.current(scope, planId, runId);
  }
}

/**
 * Records the booking in the plan command's transaction (`PlanRepository.markLeadLinkBooked`), with
 * the campaign's call host as the booker entered on the form. False when the task already holds another
 * outcome than this command's booking or a reason it could not book, which rolls the command back.
 */
export async function recordLeadLinkBooked(
  client: PoolClient,
  scope: WorkspaceScope,
  input: {
    planId: string;
    runId: string;
    commandId: string;
    actorId: string;
    start: string | null;
    zone: string | null;
    minutes: number | null;
  },
): Promise<boolean> {
  const hosts = await client.query(
    `SELECT hosts.name,hosts.email FROM lead_link_tasks tasks
     JOIN campaign_call_hosts hosts ON hosts.organization_id=tasks.organization_id
       AND hosts.workspace_id=tasks.workspace_id AND hosts.campaign_id=tasks.campaign_id
     WHERE tasks.organization_id=$1 AND tasks.workspace_id=$2 AND tasks.plan_id=$3
       AND tasks.run_id=$4`,
    [scope.organizationId, scope.workspaceId, input.planId, input.runId],
  );
  const host = hosts.rows[0] ? CallHostSchema.parse(hosts.rows[0]) : null;
  const result = await client.query(
    `UPDATE lead_link_tasks SET outcome='booked',could_not_book_reason=NULL,
       outcome_command_id=$5,outcome_by=$6,outcome_at=now(),receipt_basis='rep_attested',
       booked_start=$7,booked_zone=$8,booked_minutes=$9,host_name=$10,host_email=$11
     WHERE organization_id=$1 AND workspace_id=$2 AND plan_id=$3 AND run_id=$4
       AND (outcome IS NULL OR outcome='could_not_book'
         OR (outcome='booked' AND outcome_command_id=$5))`,
    [
      scope.organizationId,
      scope.workspaceId,
      input.planId,
      input.runId,
      input.commandId,
      input.actorId,
      input.start,
      input.zone,
      input.minutes,
      host?.name ?? null,
      host?.email ?? null,
    ],
  );
  return (result.rowCount ?? 0) === 1;
}
